import * as path from 'node:path';

export function parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === '') {
    return defaultValue;
  }
  if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }
  throw new Error(`Invalid boolean value: ${value}`);
}

export function parseList(value: string | undefined): string[] {
  return String(value ?? '')
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function parsePositiveInteger(value: string | undefined, name: string): number {
  const parsed = Number(String(value ?? '').trim());
  if (!Number.isSafeInteger(parsed) || parsed < 1) {
    throw new Error(`${name} must be a positive integer`);
  }
  return parsed;
}

export function parseNonNegativeNumber(value: string | undefined, name: string): number {
  const parsed = Number(String(value ?? '').trim());
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${name} must be a non-negative number`);
  }
  return parsed;
}

export function safeSnapshotPath({
  filename,
  rootDir,
  rootDirResolved,
  description,
}: {
  filename: string;
  rootDir: string;
  rootDirResolved: string;
  description: string;
}): { normalized: string; target: string } {
  const normalized = path.posix.normalize(filename);
  if (
    !normalized ||
    normalized === '.' ||
    normalized === '..' ||
    normalized.startsWith('../') ||
    path.posix.isAbsolute(normalized)
  ) {
    throw new Error(`Unsafe ${description} path from GitHub API: ${filename}`);
  }

  const target = path.resolve(rootDir, ...normalized.split('/'));
  if (target !== rootDirResolved && !target.startsWith(rootDirResolved + path.sep)) {
    throw new Error(`${description} path escaped review directory: ${filename}`);
  }
  return { normalized, target };
}

export function safeRepoPath(workspace: string, relativePath: string): string {
  if (path.isAbsolute(relativePath)) {
    throw new Error(`Repository path must be relative: ${relativePath}`);
  }
  const normalized = path.posix.normalize(relativePath);
  if (
    !normalized ||
    normalized === '.' ||
    normalized === '..' ||
    normalized.startsWith('../') ||
    path.posix.isAbsolute(normalized)
  ) {
    throw new Error(`Unsafe repository path: ${relativePath}`);
  }
  const workspaceResolved = path.resolve(workspace);
  const target = path.resolve(workspaceResolved, ...normalized.split('/'));
  if (target !== workspaceResolved && !target.startsWith(workspaceResolved + path.sep)) {
    throw new Error(`Repository path escaped workspace: ${relativePath}`);
  }
  return target;
}

export function looksLikeText(content: Buffer): boolean {
  return !content.includes(0);
}

export function fenceFor(text: string): string {
  let fence = '```';
  while (text.includes(fence)) {
    fence += '`';
  }
  return fence;
}

export function fencedBlock(label: string, text: string): string {
  const fence = fenceFor(text);
  return [`${fence}${label}`, text, fence].join('\n');
}

export function truncate(text: string, limit = 1200): string {
  const value = String(text || '').trim();
  return value.length <= limit ? value : `${value.slice(0, Math.max(0, limit - 3))}...`;
}

export function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
